"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.Harvest = void 0;
var _encode = require("../url/encode");
var _stringify = require("../util/stringify");
var submitData = _interopRequireWildcard(require("../util/submit-data"));
var _location = require("../url/location");
var _info = require("../config/info");
var _init = require("../config/init");
var _runtime = require("../config/runtime");
var _cleanUrl = require("../url/clean-url");
var _eventListenerOpts = require("../event-listener/event-listener-opts");
var _sharedContext = require("../context/shared-context");
var _env = require("../constants/env.npm");
var _runtime2 = require("../constants/runtime");
var _console = require("../util/console");
var _now = require("../timing/now");
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
/*
 * Copyright 2020 New Relic Corporation. All rights reserved.
 * SPDX-License-Identifier: Apache-2.0
 */

const warnings = {};

/**
 * @typedef {import('./types.js').NetworkSendSpec} NetworkSendSpec
 * @typedef {import('./types.js').HarvestEndpointIdentifier} HarvestEndpointIdentifier
 * @typedef {import('./types.js').HarvestPayload} HarvestPayload
 * @typedef {import('./types.js').FeatureHarvestCallback} FeatureHarvestCallback
 * @typedef {import('./types.js').FeatureHarvestCallbackOptions} FeatureHarvestCallbackOptions
 */
class Harvest extends _sharedContext.SharedContext {
  constructor(parent) {
    super(parent); // gets any allowed properties from the parent and stores them in `sharedContext`

    this.tooManyRequestsDelay = (0, _init.getConfigurationValue)(this.sharedContext.agentIdentifier, 'harvest.tooManyRequestsDelay') || 60;
    this.obfuscator = (0, _runtime.getRuntime)(this.sharedContext.agentIdentifier).obfuscator;
    this._events = {};
  }

  /**
   * Initiate a harvest from multiple sources. An event that corresponds to the endpoint
   * name is emitted, which gives any listeners the opportunity to provide payload data.
   * Note: Used by page_action
   * @param {NetworkSendSpec} spec Specification for sending data
   */
  sendX(spec = {}) {
    const submitMethod = submitData.getSubmitMethod({
      isFinalHarvest: spec.opts?.unload
    });
    const options = {
      retry: !spec.opts?.unload && submitMethod === submitData.xhr,
      isFinalHarvest: spec.opts?.unload === true
    };
    const payload = this.createPayload(spec.endpoint, options);
    const caller = this._send.bind(this);
    return caller({
      ...spec,
      payload,
      submitMethod
    });
  }

  /**
   * Initiate a harvest call.
   * @param {NetworkSendSpec} spec Specification for sending data
   */
  send(spec = {}) {
    const caller = this._send.bind(this);
    return caller(spec);
  }

  /**
   * Initiate a harvest call. Typically used by `sendX` and `send` methods or called directly
   * for raw network calls.
   * @param {NetworkSendSpec} param0 Specification for sending data
   * @returns {boolean} True if the network call succeeded. For final harvest calls, the return
   * value should not be relied upon because network calls will be made asynchronously.
   */
  _send({
    endpoint,
    payload = {},
    opts = {},
    submitMethod,
    cbFinished,
    customUrl,
    raw,
    includeBaseParams = true
  }) {
    const info = (0, _info.getInfo)(this.sharedContext.agentIdentifier);
    if (!info.errorBeacon) return false;
    const agentRuntime = (0, _runtime.getRuntime)(this.sharedContext.agentIdentifier);
    let {
      body,
      qs
    } = this.cleanPayload(payload);
    if (Object.keys(body).length === 0 && !opts?.sendEmptyBody) {
      // no payload body? nothing to send, just run onfinish stuff and return
      if (cbFinished) {
        cbFinished({
          sent: false
        });
      }
      return false;
    }
    const init = (0, _init.getConfiguration)(this.sharedContext.agentIdentifier);
    const protocol = init.ssl === false ? 'http' : 'https';
    const perceviedBeacon = init.proxy.beacon || info.errorBeacon;
    const endpointURLPart = endpoint !== 'rum' ? "/".concat(endpoint) : '';
    let url = "".concat(protocol, "://").concat(perceviedBeacon).concat(endpointURLPart, "/1/").concat(info.licenseKey);
    if (customUrl) url = customUrl;
    if (raw) url = "".concat(protocol, "://").concat(perceviedBeacon, "/").concat(endpoint);
    const baseParams = !raw && includeBaseParams ? this.baseQueryString(qs, endpoint) : '';
    let payloadParams = (0, _encode.obj)(qs, agentRuntime.maxBytes);
    if (!submitMethod) {
      submitMethod = submitData.getSubmitMethod({
        isFinalHarvest: opts.unload
      });
    }
    if (baseParams === '' && payloadParams.startsWith('&')) {
      payloadParams = payloadParams.substring(1);
    }
    const fullUrl = "".concat(url, "?").concat(baseParams).concat(payloadParams);
    const gzip = !!qs?.attributes?.includes('gzip');
    if (!gzip) {
      if (endpoint === 'events') {
        body = body.e;
      } else {
        body = (0, _stringify.stringify)(body);
      }
      /** Warn --once per endpoint-- if the agent tries to send large payloads */
      if (body.length > 750000 && (warnings[endpoint] = (warnings?.[endpoint] || 0) + 1) === 1) (0, _console.warn)(28, endpoint);
    }
    if (!body || body.length === 0 || body === '{}' || body === '[]') {
      // If body is null, undefined, or an empty object or array, send an empty string instead
      body = '';
    }
    const headers = [];
    headers.push({
      key: 'content-type',
      value: 'text/plain'
    });

    /* Since workers don't support sendBeacon right now, they can only use XHR method.
        Because they still do permit synch XHR, the idea is that at final harvest time (worker is closing),
        we just make a BLOCKING request--trivial impact--with the remaining data as a temp fill-in for sendBeacon.
       Following the removal of img-element method. */
    let result = submitMethod({
      url: fullUrl,
      body,
      sync: opts.unload && _runtime2.isWorkerScope,
      headers
    });
    if (!opts.unload && cbFinished && submitMethod === submitData.xhr) {
      const harvestScope = this;
      result.addEventListener('loadend', function () {
        // `this` refers to the XHR object in this scope, do not change this to a fat arrow
        // status 0 refers to a local error, such as CORS or network failure, or a blocked request by the browser (e.g. adblocker)
        const cbResult = {
          sent: this.status !== 0,
          status: this.status,
          xhr: this,
          fullUrl
        };
        if (this.status === 429) {
          cbResult.retry = true;
          cbResult.delay = harvestScope.tooManyRequestsDelay;
        } else if (this.status === 408 || this.status === 500 || this.status === 503) {
          cbResult.retry = true;
        }
        if (opts.needResponse) {
          cbResult.responseText = this.responseText;
        }
        cbFinished(cbResult);
      }, (0, _eventListenerOpts.eventListenerOpts)(false));
    }
    return result;
  }

  // The stuff that gets sent every time.
  baseQueryString(qs, endpoint) {
    const runtime = (0, _runtime.getRuntime)(this.sharedContext.agentIdentifier);
    const info = (0, _info.getInfo)(this.sharedContext.agentIdentifier);
    const ref = this.obfuscator.obfuscateString((0, _cleanUrl.cleanURL)((0, _location.getLocation)()));
    const hr = runtime?.session?.state.sessionReplayMode === 1 && endpoint !== 'jserrors';
    const qps = ['a=' + info.applicationID, (0, _encode.param)('sa', info.sa ? '' + info.sa : ''), (0, _encode.param)('v', _env.VERSION), transactionNameParam(info), (0, _encode.param)('ct', runtime.customTransaction), '&rst=' + (0, _now.now)(), '&ck=0',
    // ck param DEPRECATED - still expected by backend
    '&s=' + (runtime.session?.state.value || '0'),
    // the 0 id encaps all untrackable and default traffic
    (0, _encode.param)('ref', ref), (0, _encode.param)('ptid', runtime.ptid ? '' + runtime.ptid : '')];
    if (hr) qps.push((0, _encode.param)('hr', '1', qs));
    return qps.join('');
  }

  /**
   * Calls and accumulates data from registered harvesting functions based on
   * the endpoint being harvested.
   * @param {HarvestEndpointIdentifier} endpoint BAM endpoint identifier.
   * @param {FeatureHarvestCallbackOptions} options Options to be passed to the
   * feature harvest listener callback.
   * @returns {HarvestPayload} Payload object to transmit to the bam endpoint.
   */
  createPayload(endpoint, options) {
    const listeners = this._events[endpoint];
    const payload = {
      body: {},
      qs: {}
    };
    if (Array.isArray(listeners) && listeners.length > 0) {
      for (let i = 0; i < listeners.length; i++) {
        const singlePayload = listeners[i](options);
        if (singlePayload) {
          payload.body = {
            ...payload.body,
            ...(singlePayload.body || {})
          };
          payload.qs = {
            ...payload.qs,
            ...(singlePayload.qs || {})
          };
        }
      }
    }
    return payload;
  }

  /**
   * Cleans and returns a payload object containing a body and qs
   * object with key/value pairs. KV pairs where the value is null,
   * undefined, or an empty string are removed to save on transmission
   * size.
   * @param {HarvestPayload} payload Payload to be sent to the endpoint.
   * @returns {HarvestPayload} Cleaned payload payload to be sent to the endpoint.
   */
  cleanPayload(payload = {}) {
    const clean = input => {
      if (typeof Uint8Array !== 'undefined' && input instanceof Uint8Array || Array.isArray(input)) return input;
      if (typeof input === 'string') return input.length > 0 ? input : null;
      return Object.entries(input || {}).reduce((accumulator, [key, value]) => {
        if (typeof value === 'number' || typeof value === 'string' && value.length > 0 || typeof value === 'object' && Object.keys(value || {}).length > 0) {
          accumulator[key] = value;
        }
        return accumulator;
      }, {});
    };
    return {
      body: clean(payload.body),
      qs: clean(payload.qs)
    };
  }

  /**
   * Registers a function to be called when harvesting is triggered for a specific
   * endpoint.
   * @param {HarvestEndpointIdentifier} endpoint
   * @param {FeatureHarvestCallback} listener
   */
  on(endpoint, listener) {
    if (!Array.isArray(this._events[endpoint])) {
      this._events[endpoint] = [];
    }
    this._events[endpoint].push(listener);
  }
}

// Constructs the transaction name param for the beacon URL.
// Prefers the obfuscated transaction name over the plain text.
// Falls back to making up a name.
exports.Harvest = Harvest;
function transactionNameParam(info) {
  if (info.transactionName) return (0, _encode.param)('to', info.transactionName);
  return (0, _encode.param)('t', info.tNamePlain || 'Unnamed Transaction');
}